import { t } from '../../services/locale';
import type {
  ProfileDateFormatters,
  ProfileMembershipBranch,
  ProfileStackedMembership,
  ProfileUser,
  ProfileUserMembership,
} from './profileMembership.types';

export const DEFAULT_DATE_FORMAT = 'DD-MM-YYYY';
const LOCAL_DAY_FORMAT = 'YYYY-MM-DD';
const PAYG = 'payg';
const CREDITS = 'num_classes';

export function getBranchDateFormat(branch: ProfileMembershipBranch | null | undefined): string {
  return branch?.configuration?.formats?.date || DEFAULT_DATE_FORMAT;
}

export function getMembershipStatusTag(status: string | undefined): string {
  switch (status) {
    case 'ACTIVE':
      return 'green-tag';
    case 'PAUSED':
      return 'yellow-tag';
    case 'CANCELED':
    case 'LOCKED':
      return 'red-tag';
    case 'FUTURE':
      return 'blue-tag';
    default:
      return '';
  }
}

export function hasCurrentStackedMembership(stacked: ProfileStackedMembership[] | null | undefined): boolean {
  return (stacked ?? []).some((m) => m.status === 'ACTIVE' || m.status === 'LOCKED');
}

export function getMembershipStartDate(
  membership: ProfileStackedMembership,
  stacked: ProfileStackedMembership[] | null | undefined,
  branch: ProfileMembershipBranch | null | undefined,
  formatUnix: ProfileDateFormatters['formatUnix']
): string {
  const startDate = membership.duration?.startDate;
  const formatted = startDate ? formatUnix(startDate, getBranchDateFormat(branch)) : '';
  let label: string;
  if (membership.status === 'FUTURE') {
    label = hasCurrentStackedMembership(stacked)
      ? t('WEB_STARTS_AFTER_CURRENT_MEMBERSHIP')
      : formatted || t('WEB_START_AT_FIRST_BOOKING');
  } else if (formatted) {
    label = formatted;
  } else {
    return '';
  }
  return `${t('WEB_START_DATE')}: ${label}`;
}

export function getMembershipExpiryDate(
  membership: ProfileStackedMembership,
  branch: ProfileMembershipBranch | null | undefined,
  formatUnix: ProfileDateFormatters['formatUnix']
): string {
  if (membership.status === 'FUTURE') {
    return '';
  }
  const meta = membership.membershipMetaData;
  if (!meta?.hasEndDate) {
    return t('WEB_UNLIMITED_MEMBERSHIP');
  }
  const format = getBranchDateFormat(branch);
  if (meta.autoRenewal) {
    const expiry = membership.duration?.expiryDate;
    return `${t('WEB_AUTORENEWING_EXPIRY_DATE')}: ${expiry ? formatUnix(expiry, format) : ''}`;
  }
  const end = membership.endDate || membership.duration?.expiryDate;
  return `${t('WEB_EXPIRY_DATE')}: ${end ? formatUnix(end, format) : ''}`;
}

/**
 * `nextPaymentDate` / `revisedPaymentDate` arrive as a unix-seconds string,
 * an ISO date-time, or a `YYYY-MM-DD` date depending on the plan. Unix values
 * are treated as instants; anything else is interpreted in the branch timezone.
 */
export function formatPaymentDate(
  value: string | number | null | undefined,
  format: string,
  formatters: ProfileDateFormatters
): string {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? formatters.formatUnix(value, format) : '';
  }
  const trimmed = (value ?? '').trim();
  if (trimmed === '') {
    return '';
  }
  if (/^\d+$/.test(trimmed)) {
    return formatters.formatUnix(Number(trimmed), format);
  }
  return formatters.formatLocalDate(trimmed, format);
}

type PaymentDateValue = ProfileStackedMembership['nextPaymentDate'];

function hasPaymentDate(value: PaymentDateValue): value is string | number {
  return typeof value === 'number' || (typeof value === 'string' && value !== '');
}

/** The revised date wins over the scheduled one when set. */
function getNextPaymentSource(membership: ProfileStackedMembership): PaymentDateValue {
  return hasPaymentDate(membership.revisedPaymentDate) ? membership.revisedPaymentDate : membership.nextPaymentDate;
}

export function getSubscriptionNextPayment(
  membership: ProfileStackedMembership,
  branch: ProfileMembershipBranch | null | undefined,
  formatters: ProfileDateFormatters
): string {
  if (membership.status === 'FUTURE') {
    return '';
  }
  const source = getNextPaymentSource(membership);
  if (!hasPaymentDate(source)) {
    return '';
  }
  const formatted = formatPaymentDate(source, getBranchDateFormat(branch), formatters);
  return formatted ? `${t('WEB_NEXT_PAYMENT_DATE')}: ${formatted}` : '';
}

export function hasMembershipPauseRows(membership: ProfileStackedMembership | null | undefined): boolean {
  const pause = membership?.pause;
  return !!(pause?.pauseStartDate && pause.pauseEndDate && membership.status === 'ACTIVE');
}

function pauseRow(
  key: string,
  value: string | null | undefined,
  branch: ProfileMembershipBranch | null | undefined,
  formatters: ProfileDateFormatters
): string {
  const formatted = value ? formatters.formatLocalDate(value, getBranchDateFormat(branch)) : '';
  return `${t(key)}: ${formatted}`;
}

export function getMembershipPauseStartRow(
  membership: ProfileStackedMembership,
  branch: ProfileMembershipBranch | null | undefined,
  formatters: ProfileDateFormatters
): string {
  return hasMembershipPauseRows(membership)
    ? pauseRow('WEB_PAUSE_STARTS', membership.pause?.pauseStartDate, branch, formatters)
    : '';
}

export function getMembershipPauseEndRow(
  membership: ProfileStackedMembership,
  branch: ProfileMembershipBranch | null | undefined,
  formatters: ProfileDateFormatters
): string {
  return hasMembershipPauseRows(membership)
    ? pauseRow('WEB_PAUSE_ENDS', membership.pause?.pauseEndDate, branch, formatters)
    : '';
}

export type MembershipActionContext = {
  /** `is-click-to-cancel-email-request-enabled`. */
  isClickToCancelEnabled: boolean;
  /** `showContactStudioAboutMembership()`: the user has a membership type. */
  isContactStudioEnabled: boolean;
};

export function isPauseAllowed(membership: ProfileStackedMembership): boolean {
  if (membership.isCancellationRequested) {
    return false;
  }
  const actions = membership.allowedActions;
  return !!(actions?.isPauseAllowed || actions?.isScheduledPauseAllowed);
}

export function isCancelAllowed(membership: ProfileStackedMembership, context: MembershipActionContext): boolean {
  return context.isClickToCancelEnabled && !membership.isCancellationRequested;
}

export function showContactStudioAboutMembership(user: ProfileUser | null | undefined): boolean {
  return !!user?.membership?.type;
}

/** Manage replaces the individual pause / cancel CTAs once two or more actions apply. */
export function showManageButton(membership: ProfileStackedMembership, context: MembershipActionContext): boolean {
  if (membership.isCancellationRequested) {
    return false;
  }
  const flags = [isPauseAllowed(membership), isCancelAllowed(membership, context), context.isContactStudioEnabled];
  return flags.filter(Boolean).length >= 2;
}

export function showManageButtonForAnyMembership(
  stacked: ProfileStackedMembership[] | null | undefined,
  context: MembershipActionContext
): boolean {
  return (stacked ?? []).some((membership) => showManageButton(membership, context));
}

export function memberIsPAYGOrCredits(user: ProfileUser | null | undefined): boolean {
  const type = user?.membership?.type;
  return type === PAYG || type === CREDITS;
}

function hasSubscription(membership: ProfileUserMembership | null | undefined): boolean {
  const subscription = membership?.subscription;
  return !!subscription && Object.keys(subscription).length > 0;
}

export function membershipIsSubscription(user: ProfileUser | null | undefined): boolean {
  return hasSubscription(user?.membership);
}

function isBeforeToday(
  expiryUnix: number | null | undefined,
  todayLocal: string,
  formatUnix: ProfileDateFormatters['formatUnix']
): boolean {
  if (!expiryUnix) {
    return false;
  }
  // End-of-day comparison in the Angular code reduces to a branch-local day compare.
  return formatUnix(expiryUnix, LOCAL_DAY_FORMAT) < todayLocal;
}

/**
 * @param todayLocal today's `YYYY-MM-DD` in the branch timezone.
 */
export function membershipAlreadyExpired(
  user: ProfileUser | null | undefined,
  todayLocal: string,
  formatUnix: ProfileDateFormatters['formatUnix']
): boolean {
  const membership = user?.membership;
  if (!membership || Object.keys(membership).length === 0) {
    return false;
  }
  if (!hasSubscription(membership)) {
    return isBeforeToday(membership.expiry_date, todayLocal, formatUnix);
  }
  const subscription = membership.subscription;
  if (subscription?.end_date) {
    return subscription.auto_renewal ? false : isBeforeToday(subscription.end_date, todayLocal, formatUnix);
  }
  return false;
}
